import type { PerspectiveCamera } from 'three'
import type { RendererController } from './RendererController'

export class ViewportController {
  private readonly container: HTMLElement
  private readonly camera: PerspectiveCamera
  private readonly renderer: RendererController
  private readonly onResize: () => void
  private readonly resizeObserver: ResizeObserver
  private resizeFrame: number | null = null
  private resolutionQuery: MediaQueryList | null = null
  private disposed = false

  public constructor(
    container: HTMLElement,
    camera: PerspectiveCamera,
    renderer: RendererController,
    onResize: () => void = () => undefined,
  ) {
    this.container = container
    this.camera = camera
    this.renderer = renderer
    this.onResize = onResize
    this.resizeObserver = new ResizeObserver(this.scheduleResize)

    this.resizeObserver.observe(this.container)
    window.addEventListener('resize', this.scheduleResize, { passive: true })
    window.addEventListener('orientationchange', this.scheduleResize, { passive: true })
    this.bindResolutionQuery()
    this.resizeNow()
  }

  private readonly scheduleResize = (): void => {
    if (this.disposed || this.resizeFrame !== null) return
    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resizeFrame = null
      this.resizeNow()
    })
  }

  private readonly handleResolutionChange = (): void => {
    this.bindResolutionQuery()
    this.scheduleResize()
  }

  private bindResolutionQuery(): void {
    this.resolutionQuery?.removeEventListener('change', this.handleResolutionChange)
    this.resolutionQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)
    this.resolutionQuery.addEventListener('change', this.handleResolutionChange)
  }

  private resizeNow(): void {
    const bounds = this.container.getBoundingClientRect()
    const width = Math.max(1, Math.round(bounds.width))
    const height = Math.max(1, Math.round(bounds.height))

    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.renderer.resize(width, height, window.devicePixelRatio || 1)
    this.onResize()
  }

  public dispose(): void {
    this.disposed = true
    if (this.resizeFrame !== null) window.cancelAnimationFrame(this.resizeFrame)
    this.resizeObserver.disconnect()
    this.resolutionQuery?.removeEventListener('change', this.handleResolutionChange)
    window.removeEventListener('resize', this.scheduleResize)
    window.removeEventListener('orientationchange', this.scheduleResize)
  }
}
